import React from 'react';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import {
  CommerceAddress,
  FetchCustomerBillingProfilesForTxaInPpcQuery,
  FetchCustomerBillingProfilesForTxaInPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import type { CartLinkedEntitlementsSchema, CartProductSchema } from '../../../types';
import {
  useProductAndAppsLineItemsStore,
  useLineItemsActualEstimateStore,
  useCartProducts,
  useCartApps,
  useStore,
  useCurrentOperationTypesStore,
} from '../../../store';
import { OperationType } from '../../../types';
import { OrderSummary, CustomerContact } from './components/order-summary';
import { CurrentScreenTypeEnum } from '../../../store/types';
import { NotificationTypes, errorNotificationBuilder, useNotification } from '@css-xp/base';
import { useQuery } from 'apollo-client-ppc';
import {
  maxThresholdForResults,
  defaultBillingProfileSortOptions,
  STATSIG_FEATURE_GATES,
  CCP_SEARCH_MAX_LIMIT,
} from '@css-xp/ppc-constants';
import { fetchCustomerBillingProfilesForTxaInPPC } from './queries/fetch-billing-profiles-for-transaction-account';
import { CartCustomerSearchItem } from '../../customer/src/types';
import { truthy } from '../../../utils';
import { OrderDetailsProps } from '../../../order-page';

type OrderSummaryWrapperProps = {
  transactionAccountId: string;
  customer?: CartCustomerSearchItem;
  setSummaryDrawerOpen: (_isOpen: boolean) => void;
  onResetOrder?: () => void;
  setOrderDetails?: (_orderDetails: OrderDetailsProps) => void;
};

export const OrderSummaryWrapper: React.FC<OrderSummaryWrapperProps> = ({
  transactionAccountId,
  customer,
  setSummaryDrawerOpen,
  onResetOrder,
  setOrderDetails,
}) => {
  const [orderSummaryLineItems] = useProductAndAppsLineItemsStore();
  const [lineItemsActualEstimate] = useLineItemsActualEstimateStore();
  const currentOperationTypes = useCurrentOperationTypesStore();
  const cartProducts = useCartProducts();
  const cartApps = useCartApps();
  const [
    {
      partnerPromotions,
      linkedEntitlements,
      invoiceGroupId,
      invoiceGroupName,
      selectedPartnerAccountData,
      nextBillDate,
      selectedProducts,
      products,
      offeringsAndpricingPlans,
      selectedLinkedEntitlements,
      sandboxProducts,
      sandboxApps,
      featureFlags,
      billingProfiles,
      sandboxParentData,
    },
    actions,
  ] = useStore();

  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const showSandboxStatus = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isNonCommercialPricingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;
  const isMultiCurrencyChangesEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] || false;
  const isJPDAsJiraFamilyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY] || false;
  const isCoTermInUofEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_CO_TERM_IN_UOF] || false;
  const isSandboxAppCreateInUOFEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_APP_CREATE_IN_UOF] || false;
  const isFetchBillingProfilesViaCCPEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP] || false;
  const isDesignIteration1Enabled = !!featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1];
  const isTWCPhase2Enabled = !!featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2];
  const isAppEditionSupportEnabled = !!featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT];

  const triggerErrorNotificationForFetchIGFaliure = (message: string) => {
    if (notificationSent) {
      return;
    }
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('fetch-ig-error-notification')
      .withHeader('Billing profile data fetch failed')
      .withMessage(message)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  const billToAddressData: CommerceAddress = {
    addressLine1: selectedPartnerAccountData?.billTo?.street,
    city: selectedPartnerAccountData?.billTo?.city,
    country: selectedPartnerAccountData?.billTo?.country,
    name: selectedPartnerAccountData?.name,
    postcode: selectedPartnerAccountData?.billTo?.postalCode,
    state: selectedPartnerAccountData?.billTo?.state,
    taxId: selectedPartnerAccountData?.taxId,
  };

  // Merge selected cart product and apps customer contacts
  const productsWithCustomerContacts = [...cartProducts, ...(cartApps ?? [])].filter((product) => {
    // if the product is transfer product and not a partner entitlement, then we need to check for billing admins
    if (product?.operationType === OperationType.TRANSFER && !product?.isPartnerEntitlement) {
      {
        return !isEmpty(product?.billingAdmins);
      }
    }
    return !isEmpty(product.customerContacts);
  });

  // we are taking the customer contacts from the products and apps and then adding the billing admins for transfer products based on partner entitlement
  const getProductCustomerContacts = (product: CartProductSchema | CartLinkedEntitlementsSchema) => {
    const { customerContacts = [], isPartnerEntitlement, billingAdmins = [], operationType } = product;
    const productCustomerContacts = [...customerContacts];
    // if the product is transfer product and not a partner entitlement, then we have to add billing admins as customer contacts
    if (operationType === OperationType.TRANSFER && !isPartnerEntitlement && !isEmpty(billingAdmins)) {
      productCustomerContacts.push(...billingAdmins);
    }
    return productCustomerContacts;
  };

  // we are differentiating the customer contacts for transfer products and existing products
  // so that we can show remove button for the customer contacts in the order summary
  const removableCustomerContacts: string[] = React.useMemo(() => {
    // get the list of customer contacts and billing admins for transfer products
    const transferProducts = productsWithCustomerContacts?.filter(
      ({ operationType }) => operationType === OperationType.TRANSFER
    );
    let contactList: CartProductSchema['customerContacts'] = transferProducts?.flatMap(getProductCustomerContacts);
    // identify unique customer contact from the list of ecc for all products in selected cart items
    contactList = uniqBy(contactList, 'accountId');
    // format the contactList to update the data as per CustomerContact type supported
    const removableCustomerContactsData: Array<string> = [];
    contactList.forEach((contact) => {
      removableCustomerContactsData.push(contact?.email ?? contact?.name);
    });
    return removableCustomerContactsData;
  }, [productsWithCustomerContacts]);

  const existingCustomerContacts = React.useMemo(() => {
    let contactList: CartProductSchema['customerContacts'] = productsWithCustomerContacts?.flatMap(
      getProductCustomerContacts
    );
    // identify unique customer contact from the list of ecc for all products in selected cart items
    contactList = uniqBy(contactList, 'accountId');
    // format the contactList to update the data as per CustomerContact type supported
    const customerContactsData: Array<CustomerContact> = [];
    contactList.forEach((contact) => {
      customerContactsData.push({
        account_id: contact?.accountId ?? '',
        name: contact.name,
        email: contact?.email ?? contact?.name,
      });
    });
    return {
      loading: false, // Order summary is common for Qoutes flow where ecc is async loaded
      data: customerContactsData,
    };
  }, [productsWithCustomerContacts]);

  const billingProfilesForCustomer = useQuery<
    FetchCustomerBillingProfilesForTxaInPpcQuery,
    FetchCustomerBillingProfilesForTxaInPpcQueryVariables
  >(fetchCustomerBillingProfilesForTxaInPPC, {
    variables: {
      id: transactionAccountId,
      filterOptions: [
        {
          key: 'or',
          // sending multiple ship-to ids
          value: JSON.stringify(customer?.ids?.map((shipToId) => ({ shipToId }))),
        },
      ],
      searchOptions: {
        first: isFetchBillingProfilesViaCCPEnabled ? CCP_SEARCH_MAX_LIMIT : maxThresholdForResults,
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
      isFetchBillingProfilesViaCCPEnabled,
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    skip: !transactionAccountId || !customer?.ids || !customer?.ids.length,
  });

  const availableBillingProfilesForCustomer = React.useMemo(() => {
    return (
      (isFetchBillingProfilesViaCCPEnabled
        ? billingProfilesForCustomer?.data?.transactionAccount?.billingProfilesFromCCP?.nodes
        : billingProfilesForCustomer?.data?.transactionAccount?.billingProfiles?.nodes) ?? []
    );
  }, [billingProfilesForCustomer?.data, isFetchBillingProfilesViaCCPEnabled]);

  const isPaymentMethodNullForSelectedBillingProfile = React.useMemo(() => {
    return (
      availableBillingProfilesForCustomer?.find((billingProfile) => billingProfile?.id === invoiceGroupId)
        ?.defaultPaymentMethod === null
    );
  }, [invoiceGroupId, availableBillingProfilesForCustomer]);

  if (billingProfilesForCustomer.error) {
    triggerErrorNotificationForFetchIGFaliure('Please retry or contact Partner Service Desk for help');
  }

  const selectedPricingEligibility =
    billingProfiles?.find((profile) => profile.id === invoiceGroupId)?.shipToPriceEligibility ?? '';

  return (
    <OrderSummary
      lineItems={orderSummaryLineItems}
      products={products}
      cartApps={linkedEntitlements}
      selectedLinkedEntitlements={selectedLinkedEntitlements}
      nextBillDate={nextBillDate}
      selectedProducts={selectedProducts}
      cartProducts={cartProducts}
      partnerPromotions={partnerPromotions}
      currentOperationTypes={currentOperationTypes}
      transactionAccountId={transactionAccountId}
      invoiceGroupId={invoiceGroupId}
      invoiceGroupName={invoiceGroupName}
      lineItemsActualEstimate={lineItemsActualEstimate}
      billToAddressData={billToAddressData}
      offeringsAndpricingPlans={offeringsAndpricingPlans}
      setSummaryDrawerOpen={setSummaryDrawerOpen}
      setValidationErrors={(validationErrors) => actions.setValidationErrors(validationErrors)}
      setInvoiceGroupId={(selectedInvoiceGroupId) => actions.setInvoiceGroup(selectedInvoiceGroupId)}
      removableCustomerEmailList={removableCustomerContacts}
      existingCustomerContacts={existingCustomerContacts}
      setCurrentScreen={(currentScreen: CurrentScreenTypeEnum) => actions.setCurrentScreen(currentScreen)}
      availableBillingProfilesForCustomer={availableBillingProfilesForCustomer.filter(truthy)}
      isBillingProfilesForCustomerLoading={billingProfilesForCustomer.loading}
      refetchbillingProfilesForCustomer={billingProfilesForCustomer.refetch}
      isPaymentMethodNullForSelectedBillingProfile={isPaymentMethodNullForSelectedBillingProfile}
      onResetOrder={onResetOrder}
      fetchBillingProfilesForCustomerError={billingProfilesForCustomer.error}
      isIGChangeForFreeEntitlementEnabled={true}
      sandboxProducts={sandboxProducts}
      sandboxApps={sandboxApps}
      showSandboxStatus={showSandboxStatus || isSandboxDecouplingEnabled}
      selectedPricingEligibility={selectedPricingEligibility}
      isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
      isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
      isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
      isCoTermInUofEnabled={isCoTermInUofEnabled}
      isSandboxAppCreateInUOFEnabled={isSandboxAppCreateInUOFEnabled}
      sandboxParentData={sandboxParentData}
      isDesignIteration1Enabled={isDesignIteration1Enabled}
      isTWCPhase2Enabled={isTWCPhase2Enabled}
      setOrderDetails={setOrderDetails}
      isAppEditionSupportEnabled={isAppEditionSupportEnabled}
    />
  );
};
